import { useContext, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { OEEGauge } from "components/OEEGauge";
import { HMICachedDataContext } from "contexts/HMICachedDataContext/HMICachedDataContext";
import { HMIContext } from "contexts/HMIContext/HMIContext";
import { ShiftOptionsEnum } from "components/ShiftDropdown/ShiftDropdown.view";
import { percentageChecker } from "utilities/utilities";
import { applyPendingOverlay } from "utilities/qualityIntervals";

export enum OEESidebarView {
    OEE = "oee",
    AVAILABILITY = "availability",
    PERFORMANCE = "performance",
    QUALITY = "quality",
}

interface OEEGaugesSidebarProps {
    selectedView: OEESidebarView;
    onSelectView: (view: OEESidebarView) => void;
}

export const OEEGaugesSidebar = ({ selectedView, onSelectView }: OEEGaugesSidebarProps) => {
    const { t } = useTranslation();
    const { store } = useContext(HMICachedDataContext);
    const { shift } = useContext(HMIContext);

    const selectedShiftTimes =
        shift === ShiftOptionsEnum.current
            ? store.lastTwoShiftsEventReports?.currentShiftTimes
            : store.lastTwoShiftsEventReports?.previousShiftTimes;

    const selectedOEESummary =
        shift === ShiftOptionsEnum.current
            ? store.lastTwoShiftsEventReports?.currentShiftPerformanceSummary
            : store.lastTwoShiftsEventReports?.previousShiftPerformanceSummary;

    // Apply pending manual-quality overlay so the gauge shows the operator's
    // optimistic value immediately, before the next sync reflects it.
    // Silent — no visible indicator on the gauge itself; the indicator lives
    // in the modal.
    const machineCenterId = store.machineCenter?.machine_center_id;
    const selectedQualitySummary = useMemo(() => {
        const reports = store.lastTwoShiftsEventReports;
        const pending = store.manualQualityPending;
        if (shift === ShiftOptionsEnum.current) {
            return applyPendingOverlay(
                reports?.currentShiftQualitySummary,
                pending,
                machineCenterId,
                reports?.currentShiftStart,
                reports?.currentShiftEnd,
            );
        }
        return applyPendingOverlay(
            reports?.previousShiftQualitySummary,
            pending,
            machineCenterId,
            reports?.previousShiftStart,
            reports?.previousShiftEnd,
        );
    }, [shift, store.lastTwoShiftsEventReports, store.manualQualityPending, machineCenterId]);

    // Each factor is kept as an unrounded fraction so the OEE product can
    // compose them without compounding per-gauge rounding; the displayed
    // percentages round each fraction exactly as before.
    const availabilityFraction = useMemo(() => {
        if (!selectedShiftTimes) return null;
        if (selectedShiftTimes.shift <= 0) return null;
        if (selectedShiftTimes.downtime === 0) return 1;
        return Math.min(
            1,
            Math.max(0, (selectedShiftTimes.shift - selectedShiftTimes.downtime) / selectedShiftTimes.shift),
        );
    }, [selectedShiftTimes]);

    // Note: computed from the raw seconds (not availabilityFraction × 100) so
    // the displayed value stays bit-identical to the pre-OEE formula — e.g.
    // 5220/9000 is exactly 58%, but 0.58 × 100 floats to 57.99999999999999.
    const availabilityPercentage = useMemo(() => {
        if (!selectedShiftTimes) return null;
        if (selectedShiftTimes.shift <= 0) return null;
        if (selectedShiftTimes.downtime === 0) return 100;
        return percentageChecker(
            Math.floor((100 * (selectedShiftTimes.shift - selectedShiftTimes.downtime)) / selectedShiftTimes.shift),
        );
    }, [selectedShiftTimes]);

    const performanceFraction = useMemo(() => {
        if (!selectedOEESummary || selectedOEESummary.length === 0) return null;
        let totalWeightedRatio = 0;
        let totalRunTime = 0;
        for (const r of selectedOEESummary) {
            if (r.performance_ratio !== null && r.run_time_seconds > 0) {
                totalWeightedRatio += r.performance_ratio * r.run_time_seconds;
                totalRunTime += r.run_time_seconds;
            }
        }
        if (totalRunTime === 0) return null;
        return totalWeightedRatio / totalRunTime;
    }, [selectedOEESummary]);

    const performancePercentage = useMemo(
        () => (performanceFraction === null ? null : Math.round(performanceFraction * 100)),
        [performanceFraction],
    );

    const qualityFraction = useMemo(() => {
        if (!selectedQualitySummary || selectedQualitySummary.length === 0) return null;
        let totalGood = 0;
        let totalPieces = 0;
        for (const r of selectedQualitySummary) {
            if (r.quality_ratio !== null && r.total_count > 0) {
                totalGood += r.good_count;
                totalPieces += r.total_count;
            }
        }
        if (totalPieces === 0) return null;
        return totalGood / totalPieces;
    }, [selectedQualitySummary]);

    const qualityPercentage = useMemo(
        () => (qualityFraction === null ? null : Math.round(qualityFraction * 100)),
        [qualityFraction],
    );

    const qualityEnabled = useMemo(
        () => !!store?.machineCenter?.machine_center_oee_config?.record_quality_enabled,
        [store],
    );

    // OEE = Availability × Performance × Quality over the shift. Quality
    // recording disabled → not part of the calculation (factor 1, the Quality
    // gauge is hidden); quality enabled but not yet measured → assumed 100%
    // (factor 1) so OEE shows as soon as availability and performance exist.
    //
    // Window: all three factors are the unrounded values of the gauges beside
    // this one, so OEE always agrees with them. On the HMI they all cover
    // shift-start → now: availability from the shift-time ledger (clamped to
    // now by the backend), performance/quality from the summary rows, which
    // include the in-progress hour because the local-server merges its live
    // calculation cache into `last_two_shifts_info`. The OEE trend, by
    // contrast, walks completed hours only — so gauge and trend-mean will not
    // reconcile exactly, same as the Performance gauge vs its trend.
    const oeePercentage = useMemo(() => {
        if (availabilityFraction === null || performanceFraction === null) return null;
        const qualityFactor = qualityEnabled && qualityFraction !== null ? qualityFraction : 1;
        return Math.round(availabilityFraction * performanceFraction * qualityFactor * 100);
    }, [availabilityFraction, performanceFraction, qualityFraction, qualityEnabled]);

    const GAUGE_KEYS: { key: OEESidebarView; translationKey: string }[] = [
        { key: OEESidebarView.OEE, translationKey: "HMI.oee_sidebar.oee" },
        { key: OEESidebarView.AVAILABILITY, translationKey: "HMI.oee_sidebar.availability" },
        { key: OEESidebarView.PERFORMANCE, translationKey: "HMI.oee_sidebar.performance" },
        ...(qualityEnabled ? [{ key: OEESidebarView.QUALITY, translationKey: "HMI.oee_sidebar.quality" }] : []),
    ];

    const gaugePercentages = useMemo<Record<OEESidebarView, number | undefined | null>>(
        () => ({
            [OEESidebarView.OEE]: oeePercentage,
            [OEESidebarView.AVAILABILITY]: availabilityPercentage,
            [OEESidebarView.PERFORMANCE]: performancePercentage,
            [OEESidebarView.QUALITY]: qualityPercentage,
        }),
        [oeePercentage, availabilityPercentage, performancePercentage, qualityPercentage],
    );

    return (
        <div
            style={{
                width: 190,
                minWidth: 190,
                backgroundColor: "#F5F5F5",
                display: "flex",
                flexDirection: "column",
                justifyContent: "space-between",
                padding: "12px 6px",
                boxShadow: "4px 0px 0px rgba(62, 93, 138, 0.2)",
                position: "relative",
                zIndex: 1,
            }}
        >
            {GAUGE_KEYS.map(({ key, translationKey }) => {
                const isSelected = selectedView === key;
                return (
                    <div
                        key={key}
                        onClick={() => onSelectView(key)}
                        style={{
                            cursor: "pointer",
                            borderRadius: 6,
                            borderLeft: isSelected ? "4px solid #3E5D8A" : "4px solid transparent",
                            backgroundColor: isSelected ? "#FFFFFF" : "transparent",
                            boxShadow: isSelected ? "0 2px 8px rgba(62, 93, 138, 0.15)" : "none",
                            transition: "background-color 0.2s ease, box-shadow 0.2s ease",
                            padding: "4px 2px",
                        }}
                    >
                        <OEEGauge label={t(translationKey)} percentage={gaugePercentages[key]} />
                    </div>
                );
            })}
        </div>
    );
};
